import CropFreeIcon from '@mui/icons-material/CropFree'
import VerifiedIcon from '@mui/icons-material/Verified'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import ImageWithFallback from 'src/components/ImageWithFallback'
import PromoBadge from 'src/components/Promo/PromoBadge'
import useLogger from 'src/context/LoggerProvider/useLogger'
import useCardPrice from 'src/hooks/useCardPrice'
import useCommon from 'src/hooks/useCommon'
import useConfigSummary from 'src/hooks/useConfigSummary'
import { ILocation } from 'src/types/graphql'
import { numberRounedUpWithoutDecimals } from 'src/utils'
import { getCoverPhotoUrl, getDiscount } from 'src/utils/coverPhoto'

interface ProfileLocationCardProps {
  location: ILocation
}

const ProfileLocationCard = ({ location }: ProfileLocationCardProps) => {
  const { t } = useTranslation('common')
  const { defaultConfiguration, shortAddress } = location
  const { canStoreVehicle } = defaultConfiguration ?? {}
  const isParking = canStoreVehicle === true
  const { urlRoute } = defaultConfiguration ?? {}
  const { hasMultipleConfigurations, numConfigurations, sizeRangeText } =
    useConfigSummary(location)
  const { locMap, showSize } = useCommon()
  const locType = locMap(location)
  const logger = useLogger()

  const sizeText = sizeRangeText ?? showSize(location, true)
  const { price, isBrokerMode, showFromPrice } = useCardPrice(location)
  const discount = getDiscount(defaultConfiguration, isBrokerMode)

  const [iconBig, value, unit] = isBrokerMode
    ? [
        <CropFreeIcon className="mt-[-2.5px] mr-1 text-lg" key="iconBig" />,
        price,
        '/m²'
      ]
    : [null, price, `/${t('FILTER.MONTH')}`]

  return (
    <article className="group relative flex h-full flex-col overflow-hidden border border-ink-100/15 bg-white-500 transition-colors duration-300 hover:border-blue-500">
      <Link
        href={urlRoute ?? ''}
        className="absolute inset-0 z-10"
        aria-label={`Ver ${locType} en ${shortAddress}`}
      />

      {/* Cover Image */}
      <div className="relative aspect-[5/3] w-full overflow-hidden">
        <ImageWithFallback
          src={getCoverPhotoUrl(location)}
          alt={`${locType} ${sizeText} ${location.id}`}
          fill
          sizes="(max-width: 428px) 90vw, (max-width: 900px) 45vw, 30vw"
          style={{ objectFit: 'cover' }}
          className="transition-transform duration-500 group-hover:scale-[1.03]"
          onError={(e) => logger.error('Error loading img', e)}
        />
        {discount && (
          <div className="absolute top-3 right-4 left-4 flex">
            <PromoBadge
              promo={{ kind: 'firstMonth', ...discount }}
              placement="overlay"
            />
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className="flex flex-1 flex-col gap-2 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="truncate font-display text-[17px] leading-tight font-medium text-ink-700 md:text-[18px]">
                {locType || ''}
              </h3>
              <VerifiedIcon className="size-4 shrink-0 text-blue-500" />
            </div>
            <p className="mt-1 line-clamp-1 text-[13px] leading-relaxed text-ink-200">
              {shortAddress}
            </p>
          </div>
          {/* Parking single-config: hide; parking multi-config: spot count;
              otherwise size + count. */}
          {isParking && !hasMultipleConfigurations ? null : (
            <span className="shrink-0 border border-ink-100/15 px-2 py-0.5 text-[11px] tracking-[0.04em] whitespace-nowrap text-ink-200 uppercase">
              {isParking ? (
                <span className="text-blue-500">
                  {t('SEARCH_CARD.SPOTS', { count: numConfigurations })}
                </span>
              ) : (
                <>
                  {hasMultipleConfigurations && (
                    <span className="text-blue-500">
                      {numConfigurations}
                      <span className="mx-1 text-ink-100/40">·</span>
                    </span>
                  )}
                  {sizeText}
                </>
              )}
            </span>
          )}
        </div>

        {/* Price */}
        <div className="mt-auto flex items-end justify-between border-t border-ink-100/15 pt-3">
          <span className="font-display text-[20px] leading-none font-medium text-ink-700">
            {showFromPrice && (
              <span className="mr-1 font-sans text-[12px] tracking-[0.04em] text-ink-200 uppercase">
                {t('SEARCH_CARD.FROM_PRICE')}
              </span>
            )}
            {iconBig}
            <span className="text-blue-500">
              ${numberRounedUpWithoutDecimals(value)}
            </span>
            <span className="ml-1 font-sans text-[12px] font-normal text-ink-200">
              {unit}
            </span>
          </span>
        </div>
      </div>
    </article>
  )
}

export default React.memo(ProfileLocationCard)
